// services/uploadService.ts

import { openAPIClient } from '../openApiClient';
import type { PresignRequestServiceConfig, PresignUrlResponse } from './types';

export async function getUploadBucketUrlService({
  httpRequestConfig,
  params,
}: PresignRequestServiceConfig): Promise<PresignUrlResponse> {
  if (params === undefined || params.body === undefined) {
    throw new Error('Params and body are required');
  }

  const client = await openAPIClient(httpRequestConfig);
  const response = await client.POST('/v1/auth/autocourse/presign/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data as PresignUrlResponse;
}

export async function uploadFileToS3Service(presignedUrl: string, file: File, onProgress: (progress: number) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    // Honor a signal aborted during the presign window (before this runs): the
    // 'abort' event below won't fire for an already-aborted signal, so the PUT
    // would otherwise start despite the user having cancelled.
    if (signal.aborted === true) {
      reject(new Error('Upload cancelled'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onProgress(percentCompleted);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed'));
    };

    signal.addEventListener('abort', () => {
      xhr.abort();
      reject(new Error('Upload cancelled'));
    });

    xhr.send(file);
  });
}
